import { useState, useEffect, useCallback } from 'react'
import { FipeBrand, FipeModel, FipeYear, FipePrice } from '../types/fipe'
import { vehicleService } from '../services/vehicle'

export interface UseFipeResult {
  brands: FipeBrand[]
  models: FipeModel[]
  years: FipeYear[]
  loading: boolean
  error: string | null
  selectedBrand: FipeBrand | null
  selectedModel: FipeModel | null
  selectedYear: FipeYear | null
  setSelectedBrand: (brand: FipeBrand | null) => void
  setSelectedModel: (model: FipeModel | null) => void
  setSelectedYear: (year: FipeYear | null) => void
  clearSelection: () => void
  getPrice: () => Promise<FipePrice | null>
}

/**
 * Hook para gerenciar dados da API FIPE
 */
export const useFipe = (): UseFipeResult => {
  const [brands, setBrands] = useState<FipeBrand[]>([])
  const [models, setModels] = useState<FipeModel[]>([])
  const [years, setYears] = useState<FipeYear[]>([])
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState<string | null>(null)

  const [selectedBrand, setSelectedBrandState] = useState<FipeBrand | null>(null)
  const [selectedModel, setSelectedModelState] = useState<FipeModel | null>(null)
  const [selectedYear, setSelectedYearState] = useState<FipeYear | null>(null)

  // Carrega as marcas quando o hook é montado
  useEffect(() => {
    loadBrands()
  }, []) // Remove dependency on vehicleType as we only support one source now

  const loadBrands = useCallback(async () => {
    setLoading(true)
    setError(null)

    try {
      // Usar vehicleService em vez de fipeService
      // Isso pega as marcas do nosso backend (local files)
      const brandsData = await vehicleService.getBrands()
      setBrands(brandsData.sort((a, b) => a.nome.localeCompare(b.nome)))
    } catch (err) {
      setError('Erro ao carregar marcas de veículos')
      console.error('Erro ao carregar marcas:', err)
    } finally {
      setLoading(false)
    }
  }, [])

  const loadModels = useCallback(async (brandCode: string) => {
    if (!brandCode) {
      setModels([])
      return
    }

    setLoading(true)
    setError(null)

    try {
      const modelsData = await vehicleService.getModels(brandCode)
      setModels(modelsData.sort((a, b) => a.nome.localeCompare(b.nome)))
    } catch (err) {
      setError('Erro ao carregar modelos de veículos')
      console.error('Erro ao carregar modelos:', err)
    } finally {
      setLoading(false)
    }
  }, [])

  const loadYears = useCallback(async (brandCode: string, modelCode: string) => {
    if (!brandCode || !modelCode) {
      setYears([])
      return
    }

    setLoading(true)
    setError(null)

    try {
      const yearsData = await vehicleService.getYears(brandCode, modelCode)
      // Ordena decrescente
      setYears(yearsData)
    } catch (err) {
      setError('Erro ao carregar anos de veículos')
      console.error('Erro ao carregar anos:', err)
    } finally {
      setLoading(false)
    }
  }, [])

  const setSelectedBrand = useCallback((brand: FipeBrand | null) => {
    setSelectedBrandState(brand)
    setSelectedModelState(null)
    setSelectedYearState(null)
    setModels([])
    setYears([])

    if (brand) {
      loadModels(brand.codigo)
    }
  }, [loadModels])

  const setSelectedModel = useCallback((model: FipeModel | null) => {
    setSelectedModelState(model)
    setSelectedYearState(null)
    setYears([])

    if (model && selectedBrand) {
      loadYears(selectedBrand.codigo, model.codigo)
    }
  }, [selectedBrand, loadYears])

  const setSelectedYear = useCallback((year: FipeYear | null) => {
    setSelectedYearState(year)
  }, [])

  const clearSelection = useCallback(() => {
    setSelectedBrandState(null)
    setSelectedModelState(null)
    setSelectedYearState(null)
    setModels([])
    setYears([])
  }, [])

  const getPrice = useCallback(async (): Promise<FipePrice | null> => {
    // Current backend doesn't support price lookup yet, or we don't need it for battery search
    // Returning null or implementing if needed later
    return null
  }, [])

  return {
    brands,
    models,
    years,
    loading,
    error,
    selectedBrand,
    selectedModel,
    selectedYear,
    setSelectedBrand,
    setSelectedModel,
    setSelectedYear,
    clearSelection,
    getPrice
  }
}

/**
 * Hook específico para carros (conveniência)
 */
export const useCarFipe = () => {
  return useFipe()
}

/**
 * Hook para buscar apenas marcas de carros
 */
export const useCarBrands = () => {
  const [brands, setBrands] = useState<FipeBrand[]>([])
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState<string | null>(null)

  useEffect(() => {
    const loadBrands = async () => {
      setLoading(true)
      setError(null)

      try {
        const brandsData = await vehicleService.getBrands()
        setBrands(brandsData.sort((a, b) => a.nome.localeCompare(b.nome)))
      } catch (err) {
        setError('Erro ao carregar marcas de carros')
        console.error('Erro ao carregar marcas:', err)
      } finally {
        setLoading(false)
      }
    }

    loadBrands()
  }, [])

  return { brands, loading, error }
}

/**
 * Hook para buscar modelos de uma marca específica
 */
export const useCarModels = (brandCode: string | null) => {
  const [models, setModels] = useState<FipeModel[]>([])
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState<string | null>(null)

  useEffect(() => {
    const loadModels = async () => {
      if (!brandCode) {
        setModels([])
        return
      }

      setLoading(true)
      setError(null)

      try {
        const modelsData = await vehicleService.getModels(brandCode)
        setModels(modelsData.sort((a, b) => a.nome.localeCompare(b.nome)))
      } catch (err) {
        setError('Erro ao carregar modelos de carros')
        console.error('Erro ao carregar modelos:', err)
      } finally {
        setLoading(false)
      }
    }

    loadModels()
  }, [brandCode])

  return { models, loading, error }
}
